import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Globe,
  Smartphone,
  ShoppingCart,
  Search,
  Wrench,
  Palette,
  Share2,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const services = [
  {
    icon: Globe,
    title: "Création de site internet",
    slug: "site-internet",
    description:
      "Sites vitrines, institutionnels et landing pages modernes, rapides et optimisés pour le référencement.",
    features: [
      "Design sur mesure",
      "Responsive design",
      "Optimisation SEO",
      "CMS intuitif",
    ],
    color: "from-blue-500 to-blue-600",
  },
  {
    icon: Smartphone,
    title: "Développement d'applications",
    slug: "applications",
    description:
      "Applications web et mobiles performantes, conçues pour répondre à vos besoins métier spécifiques.",
    features: [
      "Applications web",
      "Applications mobiles",
      "PWA",
      "Intégrations API",
    ],
    color: "from-purple-500 to-purple-600",
  },
  {
    icon: ShoppingCart,
    title: "E-commerce",
    slug: "e-commerce",
    description:
      "Boutiques en ligne complètes avec toutes les fonctionnalités pour vendre efficacement.",
    features: [
      "Shopify & WooCommerce",
      "Paiement sécurisé",
      "Gestion des stocks",
      "Tableau de bord",
    ],
    color: "from-green-500 to-green-600",
  },
  {
    icon: Search,
    title: "SEO / Référencement",
    slug: "seo",
    description:
      "Stratégies de référencement naturel pour améliorer votre visibilité sur Google et générer du trafic.",
    features: [
      "Audit SEO",
      "Optimisation technique",
      "Stratégie de contenu",
      "Netlinking",
    ],
    color: "from-orange-500 to-orange-600",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    slug: "maintenance",
    description:
      "Assurez la pérennité de votre site avec nos services de maintenance et support technique.",
    features: [
      "Mises à jour",
      "Sauvegardes",
      "Monitoring",
      "Support réactif",
    ],
    color: "from-slate-500 to-slate-600",
  },
  {
    icon: Palette,
    title: "Branding & Identité",
    slug: "branding",
    description:
      "Créez une identité visuelle forte et mémorable qui reflète les valeurs de votre entreprise.",
    features: [
      "Logo & charte graphique",
      "Supports print",
      "Guidelines",
      "Refonte identité",
    ],
    color: "from-pink-500 to-pink-600",
  },
  {
    icon: Share2,
    title: "Réseaux sociaux",
    slug: "reseaux-sociaux",
    description:
      "Développez votre présence sur les réseaux sociaux et engagez votre communauté.",
    features: [
      "Stratégie sociale",
      "Création de contenu",
      "Community management",
      "Publicités",
    ],
    color: "from-cyan-500 to-cyan-600",
  },
  {
    icon: TrendingUp,
    title: "Marketing digital",
    slug: "marketing",
    description:
      "Stratégies marketing complètes pour augmenter votre visibilité et vos conversions.",
    features: ["Google Ads", "Emailing", "Analytics", "Conversion"],
    color: "from-red-500 to-red-600",
  },
];

export const Services = () => {
  const pageTitle =
    "Services Weblax | Création de site internet, SEO, e-commerce, branding et marketing digital";
  const pageDescription =
    "Découvrez les services Weblax : création de site internet, développement d'applications, e-commerce, SEO, maintenance, branding, réseaux sociaux et marketing digital.";
  const pageUrl = "https://weblax.fr/services";
  const pageImage = "https://weblax.fr/og-image.jpg";

  const collectionPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Services Weblax",
    url: pageUrl,
    description: pageDescription,
  };

  const itemListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: services.map((service, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: service.title,
      url: `https://weblax.fr/services/${service.slug}`,
    })),
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Weblax",
    url: "https://weblax.fr",
    logo: "https://weblax.fr/logo.png",
    email: "contact@weblax.fr",
    telephone: "+33685802016",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Paris",
      addressCountry: "FR",
    },
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="services agence web, création site internet, développement application, e-commerce, référencement SEO, maintenance site web, branding, réseaux sociaux, marketing digital, Weblax"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(collectionPageStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(itemListStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
      </Helmet>

      <div data-testid="services-page">
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28">
              <div className="max-w-3xl">
                <p className="weblax-caption text-primary mb-4">Nos services</p>
                <h1 className="weblax-heading-display mb-6">
                  Des solutions digitales{" "}
                  <span className="weblax-gradient-text">complètes</span>
                </h1>
                <p className="weblax-body-lg">
                  De la création de votre site internet à votre stratégie marketing,
                  nous vous accompagnons à chaque étape de votre développement digital.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="weblax-section">
          <div className="weblax-container">
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="weblax-card-interactive h-full group">
                  <CardContent className="p-6 md:p-8 h-full flex flex-col">
                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}
                      >
                        <service.icon className="w-7 h-7 text-white" />
                      </div>

                      <div className="flex-1">
                        <h2 className="font-semibold text-xl mb-2 group-hover:text-primary transition-colors">
                          {service.title}
                        </h2>
                        <p className="text-muted-foreground text-sm">
                          {service.description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-4">
                      {service.features.map((feature, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-sm text-muted-foreground"
                        >
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-2">
                      <Link
                        to={`/services/${service.slug}`}
                        className="flex items-center text-primary font-medium text-sm group-hover:gap-3 gap-2 transition-all"
                      >
                        En savoir plus
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="weblax-caption text-primary mb-3">Notre approche</p>
              <h2 className="weblax-heading-1 mb-4">Comment nous travaillons</h2>
              <p className="weblax-body-lg">
                Une méthodologie éprouvée pour des projets réussis.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  step: "01",
                  title: "Analyse",
                  desc: "Compréhension de vos besoins et objectifs",
                },
                {
                  step: "02",
                  title: "Stratégie",
                  desc: "Définition du plan d'action optimal",
                },
                {
                  step: "03",
                  title: "Création",
                  desc: "Design et développement sur mesure",
                },
                {
                  step: "04",
                  title: "Livraison",
                  desc: "Tests, optimisation et mise en ligne",
                },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="weblax-section">
          <div className="weblax-container">
            <div className="bg-primary rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Besoin d'un service sur mesure ?
                </h2>
                <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                  Chaque projet est unique. Contactez-nous pour discuter de vos
                  besoins spécifiques.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/devis">
                    <Button className="bg-white text-primary hover:bg-white/90 px-8 py-3 rounded-full font-medium">
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 px-8 py-3 rounded-full"
                    >
                      Nous contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;